import { useEffect, useRef, useState } from "react";
import useFaqT from "./useFaqT";

// Category tabs (design F2): from data, never hardcoded. Active tab = ink +
// 2px accent underline + mono count; during an active search no tab is
// active. Keyboard: arrow keys move between tabs (platform a11y decision).
//
// Mobile (<md): ONE horizontally scrolling row, no wrap, edge fade on the
// overflowing side(s), the active tab scrolls into view (design F7 note 2).
const FaqTabs = ({ tabs, activeId, onSelect }) => {
  const tr = useFaqT();
  const scrollerRef = useRef(null);
  const tabRefs = useRef({});
  const [fade, setFade] = useState({ left: false, right: false });

  const updateFades = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setFade({ left: el.scrollLeft > 2, right: max > 2 && el.scrollLeft < max - 2 });
  };

  useEffect(() => {
    updateFades();
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onResize = () => updateFades();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tabs.length]);

  // Active tab scrolls into the visible area (on load and on change).
  useEffect(() => {
    const btn = tabRefs.current[activeId];
    if (btn && typeof btn.scrollIntoView === "function") {
      btn.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [activeId]);

  const onKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const ids = tabs.map((t) => t.id);
    const idx = Math.max(0, ids.indexOf(activeId));
    const next = e.key === "ArrowRight" ? ids[(idx + 1) % ids.length] : ids[(idx - 1 + ids.length) % ids.length];
    onSelect(next);
    const btn = tabRefs.current[next];
    if (btn) btn.focus();
  };

  return (
    <div className="relative border-b border-[#EDEEF0]">
      <div
        ref={scrollerRef}
        onScroll={updateFades}
        role="tablist"
        aria-label={tr("FAQ categories")}
        className="flex items-stretch gap-0.5 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:gap-1 md:px-10 xl:px-[72px]"
      >
        {tabs.map((t) => {
          const active = t.id === activeId;
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active || (!activeId && t === tabs[0]) ? 0 : -1}
              onClick={() => onSelect(t.id)}
              onKeyDown={onKeyDown}
              className={`flex min-h-[46px] items-center gap-[7px] whitespace-nowrap border-0 border-b-2 bg-transparent px-2.5 pb-3.5 pt-4 text-[14px] font-medium hover:text-[#14171C] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F5EFF] md:px-3.5 ${
                active ? "border-[#1F5EFF] text-[#14171C]" : "border-transparent text-[#5A626E]"
              }`}
            >
              <span>{tr(t.label)}</span>
              <span className="font-faqmono text-[10px] text-[#8A919D]">{t.count}</span>
            </button>
          );
        })}
      </div>
      {/* The fades render at EVERY width, not just mobile: with the real nine
          categories the row overflows from 768 up as well (at 1440/2560 the
          1440 content cap keeps it overflowing), and the scrollbar is hidden —
          without the fade the desktop viewer gets no hint that more categories
          exist. `updateFades` only turns them on when there IS overflow, so
          nothing changes for a row that fits. */}
      {fade.left && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent"
        />
      )}
      {fade.right && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent"
        />
      )}
    </div>
  );
};

export default FaqTabs;
